import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Sentry from '@sentry/react-native';
import ApiService from 'src/data/services/ApiService';
import { MmkvStore } from 'src/data/services/MmkvStorage';
import NetworkService from 'src/data/services/NetworkService';
import SecureStorageService from 'src/data/services/SecureStorageService';
import { Platform } from 'react-native';
import Constants from 'src/core/utils/Constants';
import { isApiError } from 'src/core/models/api/ApiError';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface LegacyEvent {
	id?: number | string;
	uuid?: string;
	code?: string;
	name?: string;
	date?: string;
	pages?: unknown[];
	theme?: { data: Record<string, string> };
	logo?: string | null;
	[key: string]: unknown;
}

interface MigratedEvent {
	uuid: string;
	code: string;
	name: string;
	date: string;
	pages: unknown[];
	theme: { data: Record<string, string> };
	logo: string | null;
	[key: string]: unknown;
}

interface TimetableEntry {
	pageid: string | number;
	eventUuid: string;
}

interface BarTimetableEntry {
	item: unknown;
	eventUuid: string;
	key: string;
}

export type MigrationResult =
	| { status: 'completed' }
	| { status: 'skipped'; reason: 'already-done' }
	| { status: 'skipped'; reason: 'no-data' }
	| { status: 'deferred'; reason: 'offline' | 'api-error' };

async function readAsyncStorageJson<T>(key: string): Promise<T | null> {
	try {
		const raw = await AsyncStorage.getItem(key);
		if (!raw || raw === 'null') return null;
		return JSON.parse(raw) as T;
	} catch {
		Sentry.addBreadcrumb({ category: 'migration', message: `Corrupt JSON in key: ${key}`, level: 'warning' });
		return null;
	}
}

function deduplicateEvents(events: LegacyEvent[]): LegacyEvent[] {
	const seen = new Map<string, LegacyEvent>();

	for (const event of events) {
		const code = event.code;
		if (!code) continue;

		const existing = seen.get(code);
		if (!existing) {
			seen.set(code, event);
		} else if (!existing.uuid && event.uuid) {
			seen.set(code, event);
		}
	}

	return Array.from(seen.values());
}

function getIdentifier(event: LegacyEvent): string | null {
	if (event.uuid && UUID_REGEX.test(event.uuid)) return event.uuid;
	if (event.id) return String(event.id);
	return null;
}

async function migrateEvents(): Promise<MigratedEvent[] | 'abort'> {
	Sentry.addBreadcrumb({ category: 'migration', message: 'Starting event migration' });

	const oldEvents = await readAsyncStorageJson<LegacyEvent[]>('Events');
	const currentEvents = await readAsyncStorageJson<LegacyEvent[]>('Events2');

	const merged = [...(oldEvents ?? []), ...(currentEvents ?? [])];

	if (merged.length === 0) return [];

	const deduplicated = deduplicateEvents(merged);
	const migrated: MigratedEvent[] = [];

	for (const event of deduplicated) {
		const identifier = getIdentifier(event);

		if (!identifier) {
			Sentry.addBreadcrumb({
				category: 'migration',
				message: `Removing event without identifier: code=${event.code}`,
				level: 'warning',
			});
			continue;
		}

		try {
			const { data: apiEvent } = await ApiService.endpoints.events.getEvent({ eventUuid: identifier });

			// The backend confirmed the event exists (200), so keep it regardless of any local name drift: we migrate
			// the API's version anyway, and a logged-in user re-attaches server-side events.
			migrated.push(apiEvent as MigratedEvent);
			Sentry.addBreadcrumb({ category: 'migration', message: `Migrated event: ${apiEvent.uuid}` });
		} catch (error: unknown) {
			if (isApiError(error) && error.response?.status === 404) {
				Sentry.addBreadcrumb({
					category: 'migration',
					message: `Event not found (404): ${identifier}`,
					level: 'warning',
				});
				continue;
			}

			Sentry.captureException(error, { tags: { context: 'migration', eventIdentifier: identifier } });
			return 'abort';
		}
	}

	return migrated;
}

/**
 * Idempotent: if the MMKV-based migration has already run (older app versions stored the auth token in MMKV),
 * move the token into the Keychain/Keystore and remove the MMKV copy.
 */
async function migrateAuthTokenFromMmkvToSecureStore(): Promise<void> {
	const mmkvToken = MmkvStore.get(Constants.storageAuthToken);
	if (!mmkvToken) return;

	try {
		const read = await SecureStorageService.readAuthToken();
		if (!read.ok) {
			// Could not look: leave both copies exactly as they are and try again on the next start.
			Sentry.addBreadcrumb({
				category: 'migration',
				message: 'Auth token migration postponed: keychain unreadable',
			});

			return;
		}

		if (!read.token) {
			// Only drop the MMKV copy once the token is really in the keychain.
			const stored = await SecureStorageService.setAuthToken(mmkvToken);
			if (!stored) {
				Sentry.addBreadcrumb({
					category: 'migration',
					message: 'Auth token kept in MMKV: keychain write failed, retrying next start',
				});

				return;
			}
		}
		MmkvStore.remove(Constants.storageAuthToken);
		Sentry.addBreadcrumb({
			category: 'migration',
			message: 'Auth token migrated from MMKV to SecureStore',
		});
	} catch (error) {
		Sentry.captureException(error, { tags: { context: 'migration', step: 'authTokenMmkvToSecure' } });
	}
}

export async function runMigration(): Promise<MigrationResult> {
	if (MmkvStore.getBool('migrationComplete') === true) {
		await migrateAuthTokenFromMmkvToSecureStore();
		return { status: 'skipped', reason: 'already-done' };
	}

	Sentry.addBreadcrumb({ category: 'migration', message: 'Migration started' });

	const isConnected = await NetworkService.isConnected();
	if (!isConnected) {
		Sentry.addBreadcrumb({ category: 'migration', message: 'Offline, deferring migration' });
		return { status: 'deferred', reason: 'offline' };
	}

	const allKeys = await AsyncStorage.getAllKeys();
	if (allKeys.length === 0) {
		MmkvStore.set('migrationComplete', true);
		return { status: 'skipped', reason: 'no-data' };
	}

	const eventResult = await migrateEvents();

	if (eventResult === 'abort') {
		Sentry.addBreadcrumb({ category: 'migration', message: 'Migration aborted due to API error' });
		return { status: 'deferred', reason: 'api-error' };
	}

	// Merge with any events already written to MMKV (e.g. added while migration was deferred)
	const existingMmkvEvents = MmkvStore.getObject<MigratedEvent[]>('events') ?? [];
	const migratedUuids = new Set(eventResult.map((e) => e.uuid));
	const extraEvents = existingMmkvEvents.filter((e) => !migratedUuids.has(e.uuid));
	const allEvents = [...eventResult, ...extraEvents];

	const validUuids = new Set(allEvents.map((e) => e.uuid));

	MmkvStore.set('events', allEvents);

	const timetable = await readAsyncStorageJson<TimetableEntry[]>('MyTimeTableNew');
	if (timetable) {
		const filtered = timetable.filter((entry) => validUuids.has(entry.eventUuid));
		MmkvStore.set('myTimetable', filtered);
	}

	const barTimetable = await readAsyncStorageJson<BarTimetableEntry[]>('MyBarTimeTable');
	if (barTimetable) {
		const filtered = barTimetable.filter((entry) => validUuids.has(entry.eventUuid));
		MmkvStore.set('myBarTimetable', filtered);
	}

	const notificationsRead = await readAsyncStorageJson<(string | number)[]>('NotificationsRead');
	if (notificationsRead) {
		MmkvStore.set('notificationsRead', notificationsRead);
	}

	const forms = await readAsyncStorageJson<unknown[]>('Forms');
	if (forms) {
		MmkvStore.set('forms', forms);
	}

	const authToken = await AsyncStorage.getItem('AuthToken');
	if (authToken && authToken !== 'null' && authToken !== '') {
		const stored = await SecureStorageService.setAuthToken(authToken);
		if (!stored && Platform.OS !== 'web') {
			// AsyncStorage.clear() below removes the only other copy, and
			// migrationComplete stops this path from ever running again, so park the
			// token in MMKV: migrateAuthTokenFromMmkvToSecureStore retries it here
			// and on every later start.
			//
			// Not on web: MMKV is localStorage there, so this would leave a working session
			// token in plain text, readable by any script on the origin and surviving a
			// browser restart -- the opposite of what the web sibling's cookie flags are for.
			// Losing the token means logging in again, which is the safe half of the trade.
			MmkvStore.set(Constants.storageAuthToken, authToken);
		}
	}

	await AsyncStorage.clear();
	MmkvStore.set('migrationComplete', true);

	await migrateAuthTokenFromMmkvToSecureStore();

	Sentry.addBreadcrumb({
		category: 'migration',
		message: `Migration complete: ${eventResult.length} events migrated`,
	});

	return { status: 'completed' };
}
